'use client'

import * as React from 'react'
import Link from 'next/link'
import { useSelectedLayoutSegment } from 'next/navigation'
import { usePostHog } from 'posthog-js/react'

import { MainNavItem } from '@/types'
import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/theme-new/icons'
import { MobileNav } from '@/components/theme-new/mobile-nav'
import Image from 'next/image'
import { ChevronRight } from 'lucide-react'

interface MainNavProps {
  items?: MainNavItem[]
  children?: React.ReactNode
  pathId?: string
}

export function MainNav({ items, children, pathId }: MainNavProps) {
  const segment = useSelectedLayoutSegment()
  const [showMobileMenu, setShowMobileMenu] = React.useState<boolean>(false)
  const posthog = usePostHog()

  // Filter items based on feature flags
  const filteredItems = items?.filter(item => {
    if (!item.featureFlags || item.featureFlags.length === 0) {
      return true // Show items without feature flags
    }

    // Check if all required feature flags are enabled
    // Bootstrap provides defaults in posthog-provider.tsx, so we only hide if explicitly false
    return item.featureFlags.every(flag => {
      return posthog?.isFeatureEnabled(flag) !== false
    })
  })

  // console.log('main nav pathid', pathId);

  return (
    <>
      <div className="flex gap-6 md:gap-10 w-full">
        <Link href="/" className="hidden shrink-0 items-center space-x-2 lg:flex">
          {/* <Icons.logo /> */}
          <></>
          <span className="hidden font-bold sm:inline-block">
            {/* {siteConfig.name} */}

            <Image
              src="/img/logo/kit/sj-horizontal-navy.svg"
              alt="School Journey"
              width={150}
              height={38}
              className="dark:hidden"
            />
            <Image
              src="/img/logo/kit/sj-horizontal-white.svg"
              alt="School Journey"
              width={150}
              height={38}
              className="hidden dark:block"
            />
          </span>
        </Link>
        {filteredItems?.length ? (
          <nav className="hidden items-center gap-1 lg:flex xl:gap-2">
            {filteredItems?.map((item, index) => {
              let itemHref = item.href

              if (!pathId) {
                // console.log('no pathId!!!!!!!!!!!!');
                itemHref = itemHref?.replace(':pathId/', '')
              } else {
                // console.log('pathId!!!!!!!!!!!!', pathId);
                itemHref = itemHref?.replace(':pathId', pathId)
              }

              const isGetStarted = item.title === 'Get Started'
              const isActive = itemHref.startsWith(`/${segment}`)

              return (
                <Link
                  key={index}
                  href={item.disabled ? '#' : itemHref}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    // Hover matches the Book a demo pill: it lifts and turns marker gold with navy text.
                    'flex items-center rounded-full px-3 py-1.5 text-lg font-medium whitespace-nowrap transition-[background-color,color,box-shadow,transform] duration-300 ease-out sm:text-sm',
                    'hover:-translate-y-0.5 hover:bg-[#FFC933] hover:text-[#0F3D5F] hover:shadow-md hover:shadow-[#0F3D5F]/20 motion-reduce:hover:translate-y-0',
                    'focus-visible:bg-[#FFC933] focus-visible:text-[#0F3D5F] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F517B] dark:focus-visible:outline-[#BAD7EC]',
                    isActive ? 'bg-[#0F517B]/10 text-foreground dark:bg-white/10' : 'text-foreground/60',
                    item.disabled && 'cursor-not-allowed opacity-80',
                    isGetStarted && 'ml-auto gap-1'
                  )}
                >
                  {item.title}
                  {isGetStarted && <ChevronRight className="h-4 w-4" />}
                </Link>
              )
            })}
          </nav>
        ) : null}
        <div className="flex items-center gap-3 lg:hidden">
          <button
            type="button"
            aria-label="Open menu"
            className="flex items-center justify-center p-2 rounded-md hover:bg-accent/80 transition-colors"
            onClick={() => setShowMobileMenu(true)}
          >
            <Icons.logo className="h-6 w-6" />
          </button>
          {/* Brand Guidelines: the horizontal logo is 120px or wider; between 60 and 120px use the
              small-size horizontal, which is drawn for that size. Phones get the small one. */}
          <Link href="/" className="flex shrink-0 items-center">
            <Image
              src="/img/logo/kit/sj-horizontal-small-navy.svg"
              alt="School Journey"
              width={100}
              height={22}
              className="h-auto w-[100px] sm:hidden dark:hidden"
            />
            <Image
              src="/img/logo/kit/sj-horizontal-small-white.svg"
              alt="School Journey"
              width={100}
              height={22}
              className="hidden h-auto w-[100px] dark:block dark:sm:hidden"
            />
            <Image
              src="/img/logo/kit/sj-horizontal-navy.svg"
              alt="School Journey"
              width={150}
              height={38}
              className="hidden h-auto w-[124px] sm:block dark:sm:hidden"
            />
            <Image
              src="/img/logo/kit/sj-horizontal-white.svg"
              alt="School Journey"
              width={150}
              height={38}
              className="hidden h-auto w-[124px] dark:sm:block"
            />
          </Link>
        </div>
      </div>

      {/* Mobile Navigation Sheet */}
      {filteredItems && (
        <MobileNav items={filteredItems} open={showMobileMenu} onOpenChange={setShowMobileMenu}>
          {children}
        </MobileNav>
      )}
    </>
  )
}
